// One peer per viewer, scoped to a game and negotiation. UI/seat changes must
// never recreate the host emulator or allow stale signaling to close a new peer.
export function createGameTransport({ socket, gameId, hostId, isHost, stream, getIceServers, onStream, onStatus, Peer = RTCPeerConnection, makeStream = tracks => new MediaStream(tracks) }) {
  const peers = new Map();
  const earlyIce = new Map();
  const pending = new Map();
  let active = true;
  let available = false;
  let serial = 0;
  let lastRequest = 0;
  const valid = () => active && socket.connected;
  const close = id => {
    const record = peers.get(id);
    peers.delete(id);
    if (record) {
      record.pc.onconnectionstatechange = null;
      record.pc.ontrack = null;
      record.pc.onicecandidate = null;
      record.pc.close();
    }
  };
  const closeAll = () => { [...peers.keys()].forEach(close); earlyIce.clear(); pending.clear(); };
  const current = (id, record) => valid() && peers.get(id) === record;
  const iceKey = (id, sessionId) => `${id}:${sessionId || 'legacy'}`;
  const request = (force = false) => {
    if (!valid() || isHost || !available) return;
    const record = peers.get(hostId);
    if (record?.pc.connectionState === 'connected' && !force) return;
    if (!force && record && Date.now() - record.started < 10000) return;
    if (!force && Date.now() - lastRequest < 3000) return;
    lastRequest = Date.now();
    socket.emit('game_stream_request', { gameId });
    onStatus?.('connecting');
  };
  const fail = (id, record, message) => {
    if (!current(id, record)) return;
    close(id);
    if (!isHost) { onStream(null); onStatus?.(message || 'reconnecting'); }
  };
  const newPeer = (id, sessionId, ice) => {
    close(id);
    const pc = new Peer({ iceServers: ice.iceServers || [], iceTransportPolicy: ice.iceTransportPolicy || 'all' });
    const record = { pc, sessionId, started: Date.now(), ice: earlyIce.get(iceKey(id, sessionId)) || [], remoteStream: null };
    earlyIce.delete(iceKey(id, sessionId));
    peers.set(id, record);
    pc.onicecandidate = ({ candidate }) => {
      if (current(id, record) && candidate) socket.emit('game_stream_ice', { to: id, gameId, sessionId, candidate: candidate.toJSON?.() || candidate });
    };
    pc.onconnectionstatechange = () => {
      if (!current(id, record)) return;
      if (pc.connectionState === 'connected') {
        record.started = Date.now();
        if (!isHost) { onStatus?.('connected'); if (record.remoteStream) onStream(record.remoteStream); }
      } else if (['failed', 'closed'].includes(pc.connectionState)) fail(id, record);
      else if (pc.connectionState === 'disconnected') {
        record.started = Date.now() - 10000;
        if (!isHost) { onStatus?.('reconnecting'); onStream(null); }
      }
    };
    return record;
  };
  const drainIce = async (id, record) => {
    while (current(id, record) && record.ice.length) {
      try { await record.pc.addIceCandidate(record.ice.shift()); } catch (error) { onStatus?.(`ICE: ${error.message}`); }
    }
  };
  const onIce = ({ from, gameId: incoming, sessionId, candidate }) => {
    if (!valid() || incoming !== gameId || !candidate || (!isHost && from !== hostId)) return;
    const record = peers.get(from);
    if (!record || record.sessionId !== sessionId) {
      const key = iceKey(from, sessionId);
      if (earlyIce.size > 32) earlyIce.delete(earlyIce.keys().next().value);
      const queue = earlyIce.get(key) || [];
      if (queue.length < 64) queue.push(candidate);
      earlyIce.set(key, queue);
      return;
    }
    record.ice.push(candidate);
    if (record.pc.remoteDescription) drainIce(from, record);
  };
  const onViewer = async ({ viewerId, gameId: incoming }) => {
    if (!valid() || !isHost || incoming !== gameId || !stream?.getVideoTracks().some(track => track.readyState === 'live')) return;
    const existing = peers.get(viewerId);
    if (existing?.pc.connectionState === 'connected') return;
    if (pending.has(viewerId) || (existing && !['failed', 'disconnected', 'closed'].includes(existing.pc.connectionState) && Date.now() - existing.started < 10000)) return;
    const ticket = ++serial;
    pending.set(viewerId, ticket);
    try {
      const ice = await getIceServers();
      if (!valid() || pending.get(viewerId) !== ticket) return;
      const sessionId = `${socket.id}-${Date.now()}-${ticket}`;
      const record = newPeer(viewerId, sessionId, ice);
      stream.getTracks().forEach(track => { if (track.readyState === 'live') record.pc.addTrack(track, stream); });
      const offer = await record.pc.createOffer();
      if (!current(viewerId, record)) return;
      await record.pc.setLocalDescription(offer);
      if (current(viewerId, record)) socket.emit('game_stream_offer', { to: viewerId, gameId, sessionId, offer: record.pc.localDescription.sdp });
    } catch (error) { onStatus?.(`Connection failed: ${error.message}`); }
    finally { if (pending.get(viewerId) === ticket) pending.delete(viewerId); }
  };
  const onOffer = async ({ from, gameId: incoming, sessionId, offer }) => {
    if (!valid() || isHost || incoming !== gameId || from !== hostId || !offer) return;
    if (peers.has(from) && peers.get(from).sessionId === sessionId) return;
    const ticket = ++serial;
    pending.set(from, ticket);
    onStream(null);
    onStatus?.('connecting');
    try {
      const ice = await getIceServers();
      if (!valid() || pending.get(from) !== ticket) return;
      const record = newPeer(from, sessionId, ice);
      record.pc.ontrack = ({ streams, track }) => {
        if (!current(from, record)) return;
        const received = streams?.[0];
        if (received) record.remoteStream = received;
        else {
          if (!record.remoteStream) record.remoteStream = makeStream([]);
          if (!record.remoteStream.getTracks().includes(track)) record.remoteStream.addTrack(track);
        }
        onStream(record.remoteStream);
      };
      await record.pc.setRemoteDescription({ type: 'offer', sdp: offer });
      if (!current(from, record)) return;
      await drainIce(from, record);
      const answer = await record.pc.createAnswer();
      if (!current(from, record)) return;
      await record.pc.setLocalDescription(answer);
      if (current(from, record)) socket.emit('game_stream_answer', { to: from, gameId, sessionId, answer: record.pc.localDescription.sdp });
    } catch (error) { const record = peers.get(from); if (record?.sessionId === sessionId) fail(from, record, `Connection failed: ${error.message}`); }
    finally { if (pending.get(from) === ticket) pending.delete(from); }
  };
  const onAnswer = async ({ from, gameId: incoming, sessionId, answer }) => {
    const record = peers.get(from);
    if (!valid() || !isHost || incoming !== gameId || !record || record.sessionId !== sessionId || !answer) return;
    try {
      await record.pc.setRemoteDescription({ type: 'answer', sdp: answer });
      if (current(from, record)) await drainIce(from, record);
    } catch (error) { fail(from, record, `Connection failed: ${error.message}`); }
  };
  const reset = ({ gameId: incoming } = {}) => {
    if (incoming && incoming !== gameId) return;
    closeAll(); lastRequest = 0;
    if (!isHost) { available = false; onStream(null); onStatus?.('waiting for host'); }
  };
  const reconnect = () => {
    closeAll(); lastRequest = 0;
    if (isHost) socket.emit('game_stream_status', { gameId, ready: !!stream?.getVideoTracks().some(track => track.readyState === 'live') });
    else request(true);
  };
  const handlers = { game_stream_viewer_joined: onViewer, game_stream_offer: onOffer, game_stream_answer: onAnswer, game_stream_ice: onIce, game_stream_reset: reset, disconnect: reset, connect: reconnect };
  Object.entries(handlers).forEach(([event, fn]) => socket.on(event, fn));
  const watchdog = setInterval(() => {
    if (!isHost) request();
    else for (const [id, record] of peers) {
      if (record.pc.connectionState !== 'connected' && Date.now() - record.started > 10000) close(id);
    }
  }, 3000);
  return {
    setAvailable(ready) { const changed = available !== ready; available = ready; if (ready) request(changed); },
    close() { active = false; clearInterval(watchdog); closeAll(); Object.entries(handlers).forEach(([event, fn]) => socket.off(event, fn)); if (!isHost) onStream(null); },
  };
}
